import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { toApiError } from "src/types/api";

export interface RagCitation {
  path: string;
  score?: number;
  chunk_id?: string;
  title?: string;
  line?: number;
}

export interface RagQueryRequest {
  query: string;
  mode: "product" | "workflow" | "result" | "codebase" | "admin" | "qa" | "debug";
  top_k?: number;
  context?: Record<string, unknown>;
}

export interface RagQueryResponse {
  answer: string;
  citations?: RagCitation[];
}

export interface iRagQueryState {
  response: RagQueryResponse | null;
  isLoading: boolean;
  message: string | null;
}

const initialState: iRagQueryState = {
  response: null,
  isLoading: false,
  message: "",
};

function getErrorMessage(error: unknown): string {
  const apiError = toApiError(error);
  const status = apiError.response?.status;
  // axios raw 응답이 HTML 문자열인 경우를 감지하기 위해 원본 data도 확인
  const rawData =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { data?: unknown } }).response?.data
      : undefined;
  if (typeof rawData === "string" && /<html[\s>]/i.test(rawData)) {
    return status === 404
      ? "RAG endpoint is not available. Showing local helper reply."
      : "RAG request failed. Showing local helper reply.";
  }
  if (typeof rawData === "string") return rawData;
  const data = apiError.response?.data;
  return data?.message || data?.detail || apiError.message || "RAG request failed.";
}

export const ragQueryAPI = createAsyncThunk<RagQueryResponse, RagQueryRequest>(
  "ragQueryAPI",
  async (params, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(`${djangoEndpoint}/rag/query`, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const ragQuerySlice = createSlice({
  name: "ragQuery",
  initialState,
  reducers: {
    clearRagQuery: (state) => {
      state.response = null;
      state.message = "";
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ragQueryAPI.pending, (state) => {
        state.isLoading = true;
        state.message = "";
      })
      .addCase(ragQueryAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.response = action.payload;
      })
      .addCase(ragQueryAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload as string;
      });
  },
});

export const { clearRagQuery } = ragQuerySlice.actions;
export default ragQuerySlice.reducer;
